import React, { useState, useRef, useEffect } from "react";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileAlt,
  FaTimes,
} from "react-icons/fa";

const FileUpload = ({
  label = "Upload File",
  onChange,
  accept = "*",
  multiple = false,
  error = false,
  helperText,
}) => {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const processFiles = (selectedFiles) => {
    let updatedFiles;
    if (multiple) {
      updatedFiles = [...files, ...selectedFiles];
    } else {
      updatedFiles = selectedFiles.length > 0 ? [selectedFiles[0]] : [];
    }
    setFiles(updatedFiles);
    if (onChange) onChange(updatedFiles);
  };

  const handleFileChange = (e) => {
    processFiles(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    processFiles(Array.from(e.dataTransfer.files));
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleRemove = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    if (onChange) onChange(updatedFiles);
    fileInputRef.current.value = "";
  };

  const getFileIcon = (file) => {
    if (!file) return null;
    if (file.type.includes("pdf"))
      return <FaFilePdf className="text-[#c30f0a] text-lg" />;
    if (file.type.includes("word") || file.name.endsWith(".docx"))
      return <FaFileWord className="text-[#c30f0a] text-lg" />;
    if (file.type.includes("excel") || file.name.endsWith(".xlsx"))
      return <FaFileExcel className="text-[#c30f0a] text-lg" />;
    return <FaFileAlt className="text-[#c30f0a] text-lg" />;
  };

  return (
    <div className="w-full font-poppins">
      {label && (
        <label className="block mb-1 text-sm text-[#142b70]/70">{label}</label>
      )}

      <div
        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-md cursor-pointer bg-white hover:bg-[#fdf7f2] transition relative 
        ${error ? "border-red-500" : "border-[#a48572]/50"}`}
        onClick={files.length === 0 ? handleClick : undefined}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        {files.length === 0 ? (
          <p className="text-sm text-[#142b70]/70">
            Drag & drop file{multiple ? "s" : ""} here or{" "}
            <span className="text-[#c30f0a] font-semibold">browse</span>
          </p>
        ) : (
          <div className="flex flex-wrap gap-4 justify-center">
            {files.map((file, idx) => (
              <div key={idx} className="relative flex flex-col items-center">
                {file.type.startsWith("image/") ? (
                  <>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="max-w-[100px] max-h-[100px] rounded-md border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemove(idx)}
                      className="absolute -top-2 -right-2 bg-white border border-[#a48572]/50 rounded-full p-1 shadow hover:bg-[#fdf7f2] transition"
                    >
                      <FaTimes className="text-[#c30f0a] text-sm" />
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2 bg-[#fdf7f2] px-2 py-1 rounded-md border border-[#a48572]/30">
                    {getFileIcon(file)}
                    <p className="text-sm text-[#142b70] truncate max-w-[120px]">
                      {file.name}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleRemove(idx)}
                      className="border border-[#a48572]/50 rounded-full p-1 hover:bg-[#f5e9e2] transition"
                    >
                      <FaTimes className="text-[#c30f0a] text-sm" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {error && helperText && (
        <p className="text-red-500 text-xs mt-1">{helperText}</p>
      )}
    </div>
  );
};

export default FileUpload;
